/**
 * Responsive utilities for detecting viewport sizes
 * Use useIsMobile hook for functional components
 * Use these utilities for class components
 */

/**
 * Standard breakpoints used across the application
 * Matches existing mobile detection logic (width <= 512)
 */
export const BREAKPOINTS = {
  MOBILE: 512,
  TABLET: 768,
  DESKTOP: 1024,
  WIDE: 1440,
};

/**
 * Check if current viewport is mobile-sized
 * For class components only - use useIsMobile hook for functional components
 *
 * @param {number} breakpoint - The breakpoint to use (default: 512)
 * @returns {boolean} True if viewport width is <= breakpoint
 *
 * @example
 * // In class component
 * const isMobile = getIsMobile();
 */
export const getIsMobile = (breakpoint = BREAKPOINTS.MOBILE) => {
  return window.innerWidth <= breakpoint;
};

/**
 * Check if current viewport is tablet-sized
 * @returns {boolean} True if viewport width is <= TABLET breakpoint
 */
export const getIsTablet = () => {
  return window.innerWidth <= BREAKPOINTS.TABLET;
};

/**
 * Get the current viewport width
 * @returns {number} Current window inner width
 */
export const getViewportWidth = () => {
  return window.innerWidth;
};

/**
 * Get responsive value based on viewport
 * @param {Object} options - Object with breakpoint keys and values
 * @param {*} options.mobile - Value for mobile viewport
 * @param {*} options.tablet - Value for tablet viewport
 * @param {*} options.desktop - Value for desktop viewport (default)
 * @returns {*} The appropriate value for current viewport
 *
 * @example
 * const columns = getResponsiveValue({ mobile: 1, tablet: 2, desktop: 4 });
 */
export const getResponsiveValue = ({ mobile, tablet, desktop }) => {
  const width = window.innerWidth;
  if (width <= BREAKPOINTS.MOBILE) return mobile;
  if (width <= BREAKPOINTS.TABLET) return tablet ?? desktop;
  return desktop;
};
